/**
 * Error Classification Utility
 * 
 * Standardizes API error handling across the frontend.
 * Classifies errors into actionable categories with user-friendly messages.
 */

// Error types for classification
export const ERROR_TYPES = {
  INSUFFICIENT_CREDITS: 'insufficient_credits',
  PLAN_ENTITLEMENT: 'plan_entitlement',
  MODEL_ENTITLEMENT: 'model_entitlement',
  INVALID_PAYLOAD: 'invalid_payload',
  PROVIDER_TIMEOUT: 'provider_timeout',
  PROVIDER_RATE_LIMIT: 'provider_rate_limit',
  PROVIDER_UNSUPPORTED: 'provider_unsupported',
  TRANSIENT_FAILURE: 'transient_failure',
  AUTHENTICATION: 'authentication',
  UNKNOWN: 'unknown'
};

// Error patterns for classification
const ERROR_PATTERNS = {
  [ERROR_TYPES.INSUFFICIENT_CREDITS]: [
    'insufficient credits',
    'not enough credits',
    'credits required',
    'requires credits',
    'out of credits',
    'credits < '
  ],
  [ERROR_TYPES.PLAN_ENTITLEMENT]: [
    'upgrade your plan',
    'plan required',
    'growth plan',
    'business plan',
    'enterprise plan',
    'premium feature',
    'subscription required',
    'plan does not support'
  ],
  [ERROR_TYPES.MODEL_ENTITLEMENT]: [
    'model not available',
    'model requires',
    'model is restricted',
    'model access',
    'premium model',
    'not included in your plan'
  ],
  [ERROR_TYPES.INVALID_PAYLOAD]: [
    'invalid',
    'malformed',
    'missing required',
    'validation error',
    'bad request',
    'expected',
    'field required'
  ],
  [ERROR_TYPES.PROVIDER_TIMEOUT]: [
    'timeout',
    'timed out',
    'took too long',
    'deadline exceeded',
    'request cancelled'
  ],
  [ERROR_TYPES.PROVIDER_RATE_LIMIT]: [
    'rate limit',
    'too many requests',
    'throttled',
    'try again later',
    'slow down',
    'high demand'
  ],
  [ERROR_TYPES.PROVIDER_UNSUPPORTED]: [
    'not supported',
    'unsupported',
    'unavailable',
    'temporarily unavailable',
    'service unavailable',
    '503',
    '502'
  ],
  [ERROR_TYPES.TRANSIENT_FAILURE]: [
    'temporarily',
    'please try again',
    'connection error',
    'network error',
    'server error',
    'internal error',
    'unexpected error'
  ],
  [ERROR_TYPES.AUTHENTICATION]: [
    'unauthorized',
    'authentication',
    'token expired',
    'invalid token',
    'login required',
    '401'
  ]
};

// User-friendly messages for each error type
const USER_MESSAGES = {
  [ERROR_TYPES.INSUFFICIENT_CREDITS]: {
    title: 'Insufficient Credits',
    message: 'You need more credits to perform this action.',
    action: 'upgrade', // Suggests showing upgrade modal
    retriable: false
  },
  [ERROR_TYPES.PLAN_ENTITLEMENT]: {
    title: 'Upgrade Required',
    message: 'This feature requires a higher subscription plan.',
    action: 'upgrade',
    retriable: false
  },
  [ERROR_TYPES.MODEL_ENTITLEMENT]: {
    title: 'Model Upgrade Required',
    message: 'This AI model is available on a higher plan.',
    action: 'upgrade',
    retriable: false
  },
  [ERROR_TYPES.INVALID_PAYLOAD]: {
    title: 'Invalid Input',
    message: 'Please check your input and try again.',
    action: 'fix_input',
    retriable: false
  },
  [ERROR_TYPES.PROVIDER_TIMEOUT]: {
    title: 'Request Timeout',
    message: 'The request took too long. Please try again.',
    action: 'retry',
    retriable: true
  },
  [ERROR_TYPES.PROVIDER_RATE_LIMIT]: {
    title: 'High Demand',
    message: 'Please wait a moment and try again.',
    action: 'wait_retry',
    retriable: true
  },
  [ERROR_TYPES.PROVIDER_UNSUPPORTED]: {
    title: 'Service Unavailable',
    message: 'This feature is temporarily unavailable. No credits were used.',
    action: 'retry_later',
    retriable: true
  },
  [ERROR_TYPES.TRANSIENT_FAILURE]: {
    title: 'Temporary Issue',
    message: 'Something went wrong. Please try again.',
    action: 'retry',
    retriable: true
  },
  [ERROR_TYPES.AUTHENTICATION]: {
    title: 'Session Expired',
    message: 'Please sign in again to continue.',
    action: 'login',
    retriable: false
  },
  [ERROR_TYPES.UNKNOWN]: {
    title: 'Error',
    message: 'Something went wrong. No credits were used.',
    action: 'none',
    retriable: true
  }
};

/**
 * Classify an error based on its message or status code
 * @param {Error|Object} error - The error object from API call
 * @returns {string} Error type from ERROR_TYPES
 */
export const classifyError = (error) => {
  // Handle axios/fetch error responses
  const message = error?.response?.data?.detail 
    || error?.message 
    || error?.detail 
    || String(error);
  const statusCode = error?.response?.status || error?.status;
  
  const messageLower = message.toLowerCase();
  
  // Check status code first for quick classification
  if (statusCode === 401 || statusCode === 403) {
    return ERROR_TYPES.AUTHENTICATION;
  }
  if (statusCode === 400) {
    // Could be invalid payload or validation error
    return ERROR_TYPES.INVALID_PAYLOAD;
  }
  if (statusCode === 402) {
    // Payment required - usually credits or plan
    if (messageLower.includes('plan') || messageLower.includes('upgrade')) {
      return ERROR_TYPES.PLAN_ENTITLEMENT;
    }
    return ERROR_TYPES.INSUFFICIENT_CREDITS;
  }
  if (statusCode === 429) {
    return ERROR_TYPES.PROVIDER_RATE_LIMIT;
  }
  if (statusCode === 503 || statusCode === 502) {
    return ERROR_TYPES.PROVIDER_UNSUPPORTED;
  }
  if (statusCode === 504 || statusCode === 408) {
    return ERROR_TYPES.PROVIDER_TIMEOUT;
  }
  
  // Check message patterns
  for (const [errorType, patterns] of Object.entries(ERROR_PATTERNS)) {
    for (const pattern of patterns) {
      if (messageLower.includes(pattern.toLowerCase())) {
        return errorType;
      }
    }
  }
  
  return ERROR_TYPES.UNKNOWN;
};

/**
 * Get user-friendly error information
 * @param {Error|Object} error - The error object
 * @param {Object} user - User object (for admin check)
 * @returns {Object} { type, title, message, action, retriable, originalMessage }
 */
export const getErrorInfo = (error, user = null) => {
  // Admin users should see minimal errors
  if (user?.is_admin) {
    const originalMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
    return {
      type: 'admin_bypass',
      title: 'Admin Error',
      message: `(Admin) ${originalMessage}`,
      action: 'none',
      retriable: true,
      originalMessage
    };
  }
  
  const errorType = classifyError(error);
  const errorInfo = USER_MESSAGES[errorType] || USER_MESSAGES[ERROR_TYPES.UNKNOWN];
  const originalMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
  
  return {
    type: errorType,
    title: errorInfo.title,
    message: errorInfo.message,
    action: errorInfo.action,
    retriable: errorInfo.retriable,
    originalMessage
  };
};

/**
 * Check if error is retriable
 * @param {Error|Object} error - The error object
 * @returns {boolean}
 */
export const isRetriableError = (error) => {
  const errorType = classifyError(error);
  return USER_MESSAGES[errorType]?.retriable || false;
};

/**
 * Check if error requires upgrade
 * @param {Error|Object} error - The error object
 * @returns {boolean}
 */
export const requiresUpgrade = (error) => {
  const errorType = classifyError(error);
  return [
    ERROR_TYPES.INSUFFICIENT_CREDITS,
    ERROR_TYPES.PLAN_ENTITLEMENT,
    ERROR_TYPES.MODEL_ENTITLEMENT
  ].includes(errorType);
};

/**
 * Get a toast-friendly error message
 * @param {Error|Object} error - The error object
 * @param {string} defaultMessage - Fallback message
 * @returns {string}
 */
export const getToastMessage = (error, defaultMessage = 'Something went wrong. Please try again.') => {
  const errorType = classifyError(error);
  
  // If backend provided a user-friendly message, use it
  const backendMessage = error?.response?.data?.detail;
  if (backendMessage && !backendMessage.toLowerCase().includes('traceback')) {
    return backendMessage;
  }
  
  return USER_MESSAGES[errorType]?.message || defaultMessage;
};

export default {
  ERROR_TYPES,
  classifyError,
  getErrorInfo,
  isRetriableError,
  requiresUpgrade,
  getToastMessage
};
